import type { Session } from "@supabase/supabase-js";
import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "../services/supabase/Supabase";
import { ThemeToggle } from "./ThemeToggle";
import { useHiddenDashboards } from "../lib/hiddenDashboards";
import { useRegion, type Region } from "../lib/regions";

type NavEntry = { id: string; label: string; icon: string; badge?: string };
type NavGroup = { group: string; entries: NavEntry[] };

const NAV: NavGroup[] = [
  {
    group: "Overview",
    entries: [{ id: "home", label: "Home", icon: "ti-home" }],
  },
  {
    group: "Growth",
    entries: [
      { id: "leads", label: "Leads", icon: "ti-address-book" },
      { id: "mrr-sandbox", label: "MRR Tracking", icon: "ti-coin" },
      { id: "demo-funnel", label: "Demo Conversion", icon: "ti-presentation" },
      { id: "signup-funnel", label: "Sign-up Activation", icon: "ti-chart-dots" },
      { id: "attributions", label: "Attributions", icon: "ti-arrows-split" },
      { id: "marloo-wau", label: "Weekly Active Users", icon: "ti-users" },
    ],
  },
  {
    group: "Product",
    entries: [
      { id: "onboarding-baseline", label: "Onboarding Baseline", icon: "ti-flag" },
    ],
  },
  {
    group: "Retention",
    entries: [
      { id: "churn-leading-indicators", label: "Churn leading indicators", icon: "ti-alert-triangle" },
      { id: "payment-failures", label: "Payment Failures", icon: "ti-credit-card-off" },
    ],
  },
  {
    group: "Operations",
    entries: [
      { id: "llm-usage", label: "LLM Usage", icon: "ti-robot" },
      { id: "embedding-comparison", label: "Embedding Comparison", icon: "ti-arrows-diff" },
      { id: "document-qa", label: "Document QA", icon: "ti-clipboard-check" },
    ],
  },
  {
    group: "Misc",
    entries: [
      { id: "misc-document-generation", label: "Document Generation", icon: "ti-file-text" },
    ],
  },
];

function userDisplay(session: Session) {
  const email = session.user.email ?? "";
  const meta = session.user.user_metadata ?? {};
  const fullName = String(meta.full_name ?? meta.name ?? "").trim();
  const localPart = email.split("@")[0] ?? "";
  const name = fullName || localPart || "Signed-in user";
  const parts = (fullName || localPart).split(/[\s@._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? parts[0]?.[1] ?? "";
  const initials = (first + second).toUpperCase() || "?";
  return { name, email, initials };
}

type Props = {
  session: Session;
  page: string;
  onNavigate: (page: string) => void;
  children: ReactNode;
};

export function AppShell({ session, page, onNavigate, children }: Props) {
  return (
    <div className="app">
      <Sidebar page={page} onNavigate={onNavigate} session={session} />
      <div className="main">{children}</div>
    </div>
  );
}

function Sidebar({
  page,
  onNavigate,
  session,
}: {
  page: string;
  onNavigate: (page: string) => void;
  session: Session;
}) {
  const user = userDisplay(session);
  const { isHidden } = useHiddenDashboards();
  const filteredNav = NAV
    .map((g) => ({ ...g, entries: g.entries.filter((e) => e.id === "home" || !isHidden(e.id)) }))
    .filter((g) => g.entries.length > 0);
  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img className="brand-logo" src="/marloo-logo.svg" alt="Marloo" />
        <div className="env">Internal</div>
      </div>
      {filteredNav.map((g) => (
        <Fragment key={g.group}>
          <div className="sidebar-section">{g.group}</div>
          {g.entries.map((e) => (
            <button
              key={e.id}
              type="button"
              className={`nav-item ${page === e.id ? "is-active" : ""}`}
              onClick={() => onNavigate(e.id)}
            >
              <i className={`ti ${e.icon}`} />
              <span>{e.label}</span>
              {e.badge ? <span className="nav-badge">{e.badge}</span> : null}
            </button>
          ))}
        </Fragment>
      ))}
      <div className="sidebar-footer">
        <div className="user-chip">
          <div className="avatar">{user.initials}</div>
          <div className="user-meta">
            <div className="u-name">{user.name}</div>
            <div className="u-email">{user.email}</div>
          </div>
          <button
            type="button"
            onClick={() => supabase.auth.signOut()}
            title="Sign out"
            className="signout-btn"
          >
            <i
              className="ti ti-logout"
              style={{ color: "var(--fg-3)", fontSize: 14 }}
            />
          </button>
        </div>
      </div>
    </aside>
  );
}

const ENV_BADGES: Record<string, { label: string; bg: string; fg: string }> = {
  production: { label: "PROD", bg: "#a8c7c1", fg: "#2f4744" },
  staging: { label: "STAGING", bg: "#c4d8db", fg: "#3a5560" },
  development: { label: "DEV", bg: "#d9c89e", fg: "#5c4a23" },
  localhost: { label: "LOCAL", bg: "#bccdb4", fg: "#3a4d33" },
};

function EnvBadge() {
  const env = import.meta.env.VITE_ENVIRONMENT as string | undefined;
  const badge = env ? ENV_BADGES[env] : undefined;
  if (!badge) return null;
  return (
    <span
      style={{
        background: badge.bg,
        color: badge.fg,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.5,
        padding: "3px 8px",
        borderRadius: 6,
        marginRight: 8,
      }}
    >
      {badge.label}
    </span>
  );
}

export function Topbar({
  crumbs,
  right,
}: {
  crumbs: string[];
  right?: ReactNode;
}) {
  return (
    <div className="topbar">
      <div className="crumbs">
        {crumbs.map((c, i) => (
          <Fragment key={i}>
            {i > 0 ? <span className="sep">/</span> : null}
            <span className={i === crumbs.length - 1 ? "crumb-current" : undefined}>
              {c}
            </span>
          </Fragment>
        ))}
      </div>
      <div className="spacer" />
      <EnvBadge />
      <RegionToggle />
      {right}
      <ThemeToggle />
    </div>
  );
}

function RegionToggle() {
  const { region, setRegion, regions, isAllowed } = useRegion();
  if (regions.length < 2) return null;
  return (
    <div
      role="group"
      aria-label="Data region"
      style={{
        display: "inline-flex",
        background: "var(--bg-2)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: 2,
        marginRight: 8,
      }}
    >
      {regions.map((r) => {
        const active = r.id === region.id;
        const allowed = isAllowed(r.id);
        if (!allowed) {
          return (
            <DisabledRegionPill
              key={r.id}
              region={r}
              tooltip={`${r.label} not available on your account. Ask an admin to grant you a region role for ${r.label}.`}
            />
          );
        }
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => setRegion(r.id)}
            title={`Query ${r.label} data (${r.baseUrl})`}
            style={{
              background: active ? "var(--bg-3)" : "transparent",
              color: active ? "var(--fg-1)" : "var(--fg-3)",
              border: "none",
              borderRadius: 4,
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              cursor: active ? "default" : "pointer",
            }}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function DisabledRegionPill({ region, tooltip }: { region: Region; tooltip: string }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };
  useEffect(() => () => cancelClose(), []);

  return (
    <span
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      onFocus={() => { cancelClose(); setOpen(true); }}
      onBlur={scheduleClose}
      style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
    >
      <span
        tabIndex={0}
        role="button"
        aria-disabled="true"
        aria-label={`${region.label} (unavailable)`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          background: "transparent",
          color: "var(--fg-3)",
          border: "none",
          borderRadius: 4,
          padding: "3px 10px",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          cursor: "help",
          opacity: 0.45,
        }}
      >
        {region.label}
        <i className="ti ti-help-circle" style={{ fontSize: 12, lineHeight: 1 }} />
      </span>
      {open && (
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 30,
            minWidth: 240,
            width: "max-content",
            maxWidth: "min(320px, calc(100vw - 32px))",
            background: "var(--bg-elevated, #fff)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.45,
            color: "var(--fg-1)",
            textTransform: "none",
            letterSpacing: "normal",
            whiteSpace: "normal",
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
          }}
        >
          {tooltip}
        </div>
      )}
    </span>
  );
}
